/**
 * The ranked list, one page at a time. The server orders it — tier, then the strength of the evidence,
 * then fit — so this table never sorts; its order is the ranking.
 *
 * Prioritized rows show the top reason; low-fit, not-enough-data and excluded rows show why they are
 * not ranked instead. Choosing a row opens the prospect's panel. The CRM link opens the record itself.
 *
 * While the next page or a new filter loads, the rows on screen are the old ones. They are dimmed and
 * the count line says the list is updating, so the old rows are not taken for the new answer.
 */
import { Button, Spinner, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@design-system';
import { ExternalLink } from 'lucide-react';
import type { Prospecting } from '@client/api/types';
import { AGE_BAND_LABELS, ASSETS_BAND_LABELS, formatDate, fullName, priorityCopy } from '../lib/labels';
import { PriorityBadge } from './priority-badge';

interface ProspectsTableProps {
  items: Prospecting.ProspectListItem[];
  /** The CRM the book is from, as shown to the advisor. */
  crmName: string;
  view: Prospecting.ProspectView;
  page: number;
  pageSize: number;
  total: number;
  /** The rows shown are the previous page or filter's while the one asked for loads. */
  isRefreshing?: boolean;
  onOpen: (prospectId: string) => void;
  onPageChange: (page: number) => void;
}

function whyText(item: Prospecting.ProspectListItem): string {
  if (item.status === 'excluded') return item.exclusionLabel ?? 'Excluded';
  if (item.status === 'insufficientData') return item.insufficientLabel ?? 'Not enough data';
  if (item.tier === 'untiered') return item.untieredLabel ?? item.topReason ?? '—';
  return item.topReason ?? '—';
}

export function ProspectsTable({ items, crmName, view, page, pageSize, total, isRefreshing = false, onOpen, onPageChange }: ProspectsTableProps) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const showTier = view === 'tiered';

  return (
    <div
      className="flex flex-col gap-3"
      aria-busy={isRefreshing}
    >
      <Table className={isRefreshing ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
        <TableHeader>
          <TableRow>
            <TableHead>Contact</TableHead>
            {showTier && <TableHead>{priorityCopy.column}</TableHead>}
            <TableHead>{showTier ? 'Top reason' : 'Why not ranked'}</TableHead>
            <TableHead className="hidden md:table-cell">Age</TableHead>
            <TableHead className="hidden md:table-cell">Assets</TableHead>
            <TableHead className="hidden lg:table-cell">Last met</TableHead>
            <TableHead className="w-10">
              <span className="sr-only">Open in {crmName}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map(item => (
            <TableRow key={item.prospectId} className="cursor-pointer" onClick={() => onOpen(item.prospectId)}>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium text-gray-900">{fullName(item)}</span>
                  <span className="text-xs text-gray-500">{item.email}</span>
                </div>
              </TableCell>
              {showTier && (
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <PriorityBadge tier={item.tier} />
                    {item.cappedFromTier1 && <span className="text-xs text-gray-500">{priorityCopy.movedFromHigh}</span>}
                  </div>
                </TableCell>
              )}
              <TableCell className="max-w-md text-sm text-gray-700">{whyText(item)}</TableCell>
              <TableCell className="hidden md:table-cell">{item.ageBand ? AGE_BAND_LABELS[item.ageBand] : '—'}</TableCell>
              <TableCell className="hidden md:table-cell">
                {item.assetsBand ? ASSETS_BAND_LABELS[item.assetsBand] : '—'}
                {item.assetsBand && item.assetsBasis === 'firmRecorded' && (
                  <span className="block text-xs text-gray-500">held at your firm</span>
                )}
              </TableCell>
              <TableCell className="hidden lg:table-cell">{formatDate(item.lastMeetingOn)}</TableCell>
              <TableCell>
                {item.crmUrl && (
                  <a
                    href={item.crmUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={event => event.stopPropagation()}
                    aria-label={`Open ${fullName(item)} in ${crmName}`}
                    className="text-gray-500 hover:text-primary"
                  >
                    <ExternalLink className="size-4" />
                  </a>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <span
          role="status"
          className="flex items-center gap-2"
        >
          {isRefreshing ? (
            <>
              <Spinner aria-hidden="true" />
              Updating the list…
            </>
          ) : (
            <>
              {total.toLocaleString('en-US')} contact{total === 1 ? '' : 's'}
            </>
          )}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            Previous
          </Button>
          <span>
            Page {page} of {pages}
          </span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPageChange(page + 1)}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
